import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import apiClient from "../utils/apiClient";
import { HeartIcon as OutlineHeartIcon, PlusIcon, ShareIcon, FireIcon, PencilSquareIcon, PencilIcon, ClipboardDocumentListIcon} from "@heroicons/react/24/outline";
import { HeartIcon as SolidHeartIcon } from "@heroicons/react/24/solid";
import { toast } from 'react-toastify';
import AddToMealPlanModal from "./AddToMealPlanModal";
import { CalendarDaysIcon, PhotoIcon } from "@heroicons/react/24/outline";
import AddToCalendarModal from "./AddToCalendarModal";
import RecipeRating from "./RecipeRating";
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import WeeklyPlannerModal from "./WeeklyPlannerModal";
//import { useRecipeWithImage } from "../hooks/useRecipeWithImage";
import { useAuth } from "../contexts/authContext";
import { requestAppRating } from '../utils/requestAppRating';
import { useAds } from "../contexts/AdIntegration";
import { InAppReview } from '@capacitor-community/in-app-review';
import ReactMarkdown from 'react-markdown';

export default function RecipeCard({ recipe }) {
  const [checked, setChecked] = useState(
    recipe.ingredients?.map(() => false) || []
  );
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [activeTab, setActiveTab] = useState("ingredients"); // or "instructions"

  const liveRecipe = recipe;
  //const { recipe: liveRecipe, loading } = useRecipeWithImage(recipe._id, recipe);
  const { token, isLoggedIn } = useAuth();

  //const isLoggedIn = !!token;
  const [showMealModal, setShowMealModal] = useState(false);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const toggle = (index) => {
    setChecked((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const { showInterstitial } = useAds();
  useEffect(() => { showInterstitial(); }, []);

  const [editingServings, setEditingServings] = useState(false);
  //const [servingCount, setServingCount] = useState(recipe.servings || 2);
  //const [servingInput, setServingInput] = useState(servingCount.toString()); // user input
  const [servingInput, setServingInput] = useState(recipe.servings ? String(recipe.servings) : "2"); // Ensure it's a string for input value
  useEffect(() => {
    setServingInput(recipe.servings ? String(recipe.servings) : "2");
  }, [recipe.servings]); // Re-run if recipe.servings changes

  const handleGo = () => {
    const parsed = parseInt(servingInput, 10);
    if (!isNaN(parsed) && parsed > 0) {
      //setServingCount(parsed); // update stored count if needed
      navigate(`/recipe/${recipe._id}?serve=${parsed}`);
    } else {
      toast.error("Please enter a valid number greater than 0.");
    }
  };

  const copyChecklistStandard = () => {
    const text = recipe.ingredients
      .map((i, idx) => 
      //`${checked[idx] ? "☑" : "⬜"} ${i}`
      i.isHeading ? (
        i.text
        ) : (
          `${checked[idx] ? "☑" : "⬜"} ${i.text}`
        )
      )
      .join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Checklist copied!");
  };

  const copyChecklist = async () => {
    // 1. Build the HTML version
    const html = recipe.ingredients
      .map((item, idx) => {
        if (item.isHeading) {
          // Render section headings as <h3>
          return `<h3 style="margin-top:1em;">${item.text}</h3>`;
        }
        // Render each ingredient as a disabled checkbox + label
        const checkedAttr = checked[idx] ? 'checked' : '';
        return `
          <p style="margin:0.25em 0;">
            <input type="checkbox" ${checkedAttr} disabled />
            <label>${item.quantity} ${item.text}</label>
          </p>
        `;
      })
      .join('');
  
    // 2. Build the plain-text fallback
    const text = recipe.ingredients
      .map((item, idx) => {
        if (item.isHeading) {
          // Prefix headings with a blank line for readability
          return `\n${item.text.toUpperCase()}`;
        }
        return `${item.quantity} ${item.text}`;
      })
      .join('\n');
  
    // 3. Try the Clipboard API with both formats
    try {
      const blobMap = {
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      };
      const clipboardItem = new ClipboardItem(blobMap);
      await navigator.clipboard.write([clipboardItem]);
      toast.success('Rich-text checklist copied!');
    } catch (err) {
      // Fallback: just copy plain text
      console.warn('Rich-text copy failed, falling back to plain text', err);
      await navigator.clipboard.writeText(text);
      toast.success('Checklist copied as plain text.');
    }
  };

  useEffect(() => {
    const checkIfSaved = async () => {
      try {
        const res = await apiClient.get(`/users/favourites`);
        const alreadySaved = res.data.some(
          (r) => r._id === recipe._id
        );
        setSaved(alreadySaved);
      } catch (err) {
        if(err.response?.status === 401){
          //failed auth
        }
        console.error("Failed to check favourites:", err);
      }
    };

    if (isLoggedIn && recipe._id) {
      checkIfSaved();
    }
  }, [recipe._id]);

  const saveRecipe = async () => {
    try {
      const res = await apiClient.post(
        `/users/favourites`,
        { recipe }
      );
      setSaved(true);
      if (res.data.shouldPrompt) {
        //requestAppRating(); // Show prompt only when backend says so
        if (Capacitor.isNativePlatform()) {
          InAppReview.requestReview();
        }
      }
      
    } catch (err) {
      console.error("Save error:", err);

      if (err.response?.status === 403) {
        toast.warning(
          <div className="flex flex-col gap-2">
            <div>You've reached your favourite recipes limit.</div>
            <button
              onClick={() => navigate("/subscribe")}
              className="bg-sage text-white px-3 py-1 rounded hover:bg-sage/90"
            >
              Subscribe now
            </button>
          </div>,
          {
            autoClose: false, // keep it visible until they act
            closeOnClick: true,
          }
        );
      }

    }
  };

  const removeRecipe = async () => {
    try {
      await apiClient.delete(`/users/favourites/${recipe._id}`);
      setSaved(false);
    } catch (err) {
      console.error("Remove error:", err);
    }
  };

  const shareRecipe = async (recipe) => {
    const url = `https://app.sous.uk/recipe-share/${recipe._id}`;
    const title = recipe.name;
    const text = `Check out this recipe: ${recipe.summary}`;
  
    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({ title, text, url });
      } else if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Copied to clipboard!");
      }
    } catch (err) {
      if (err?.name === "AbortError" || err.message === "Share canceled") {
        // fine share was cancled.
      } else {
        console.error("Error sharing recipe:", err);
        toast.error("Something went wrong sharing this recipe.");
      }
    }
  };

  const [showMealMenu, setShowMealMenu] = useState(false);
  const [mealPlans, setMealPlans] = useState([]);
  const [newMealName, setNewMealName] = useState("");

  useEffect(() => {
    const fetchMealPlans = async () => {
      try {
        const res = await apiClient.get('/mealplans');
        setMealPlans(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          // User is not authenticated; skip setting meal plans
        } else {
          console.error('Failed to fetch meal plans:', err);
        }
      }
    };
  
    if (isLoggedIn) {
      fetchMealPlans();
    }
  }, [isLoggedIn]);

  if (!liveRecipe) {
    return (
      <div className="border p-4 rounded shadow bg-white h-full flex items-center justify-center text-gray-400">
        Loading recipe...
      </div>
    );
  }

  return (
    
    <div className="rounded bg-white pb-4">

      <div className="relative w-full h-80 md:h-48 overflow-hidden rounded-md">
        {liveRecipe.image ? (
          <img
            src={liveRecipe.image}
            alt={liveRecipe.name}
            className="w-full h-full object-cover opacity-0 transition-opacity duration-700"
            onLoad={(e) => (e.currentTarget.style.opacity = 1)}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center animate-pulse">
            <PhotoIcon className="h-8 w-8 text-gray-400" />
          </div>
        )}

        {/* Dark gradient overlay at bottom */}
        <div className="absolute bottom-0 w-full h-20 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

        {/* Action buttons */}
        {isLoggedIn && (
          <div className="absolute top-2 right-2 flex gap-2 z-10">
            <button
              onClick={saved ? removeRecipe : saveRecipe}
              className="p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow"
              title={saved ? "Remove from favourites" : "Save to favourites"}
            >
              {saved ? (
                <SolidHeartIcon className="h-5 w-5 text-red-500" />
              ) : (
                <OutlineHeartIcon className="h-5 w-5 text-gray-600 hover:text-red-500" />
              )}
            </button>

            <button
              onClick={() => setShowMealModal(true)}
              className="p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow"
              title="Add to Meal Plan"
            >
              <PlusIcon className="h-5 w-5 text-gray-600 hover:text-sage" />
            </button>
            <AddToMealPlanModal
              isOpen={showMealModal}
              onClose={() => setShowMealModal(false)}
              recipeId={recipe._id}
            />

            <button
              onClick={() => setShowCalendarModal(true)}
              className="p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow"
              title="Add to Calendar"
            >
              <CalendarDaysIcon className="h-5 w-5 text-gray-600 hover:text-sage" />
            </button>
            <WeeklyPlannerModal
              isOpen={showCalendarModal}
              onClose={() => setShowCalendarModal(false)}
              recipeId={recipe._id}
            />

            <button
              onClick={() => shareRecipe(recipe)}
              className="p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow"
              title="Share this recipe"
            >
              <ShareIcon className="h-5 w-5 text-gray-600 hover:text-sage" />
            </button>
          </div>
        )}

      </div>

      <title>{recipe.name}</title>
      <meta name="description" content={recipe.summary}/>
      <meta property='og:type' content='article' />
      <meta property='og:title' content={recipe.name} />
      <meta property='og:description' content={recipe.summary} />
      <meta property='og:image' content={"/logo512.png"} />
      
      <div>
        <div className="w-full">
          <h2 className="mt-4 text-xl font-bold flex-1 break-words">{recipe.name}</h2>
        </div>
        
        <div className="mt-1">
          <RecipeRating recipeId={recipe._id} initialRating={recipe.rating} />
        </div>
      </div>
      {/* meal plan pop up */}
      <div className="relative mt-2">
        
        {showMealMenu && (
          <div className="absolute z-10 mt-2 w-full bg-white border rounded shadow p-3 space-y-2">
            <div>
              <h4 className="font-semibold text-sm mb-1">Add to Meal Plan</h4>
              {mealPlans.length > 0 ? (
                mealPlans.map(plan => (
                  <button
                    key={plan._id}
                    onClick={async () => {
                      await apiClient.put(`/mealplans/${plan._id}`, {
                        recipeId: recipe._id,
                      });
                      toast.success(`Added to '${plan.name}'`);
                      setShowMealMenu(false);
                    }}
                    className="text-left w-full text-sm text-blue-600 hover:underline"
                  >
                    {plan.name}
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-500">No meal plans yet</p>
              )}
            </div>
      
            <div className="pt-2 border-t">
              <input
                type="text"
                placeholder="New meal plan name"
                className="border px-2 py-1 rounded w-full text-sm"
                value={newMealName}
                onChange={(e) => setNewMealName(e.target.value)}
              />
              <button
                onClick={async () => {
                  if (!newMealName.trim()) return;
                  const res = await apiClient.post(`/mealplans`, {
                    name: newMealName,
                    recipeIds: [recipe._id],
                  });
                  toast.success(`Created and added to '${res.data.name}'`);
                  setMealPlans(prev => [...prev, res.data]);
                  setNewMealName("");
                  setShowMealMenu(false);
                }}
                className="mt-1 text-sm text-green-600 hover:underline"
              >
                ➕ Create & Add
              </button>
            </div>
          </div>
        )}
      </div>
      {/* end of meal plan */}

      <p className="italic">{recipe.summary}</p>
      <div className="text-sm text-gray-600">
        <div className="grid grid-cols-3 items-center text-center py-3">
          <div>Prep</div>
          <div>Cook</div>
          <div>Serves</div>

          <div className="border-r border-gray-400">
            <b>{recipe.prepTime}</b>
          </div>
          <div className="border-r border-gray-400">
            <b>{recipe.cookTime}</b>
          </div>
          <div>
            <button
              onClick={() => setEditingServings(!editingServings)}
              className="w-full flex items-center justify-center gap-1 py-2 rounded text-charcoal hover:bg-gray-100 hover:text-sage transition-colors"
            >
              <span className="font-bold">{recipe.servings}</span>
              <PencilIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/*Serving size selector */}
      {editingServings && (
        <div className="flex w-full items-center gap-2 mt-2">
          <label className="text-sm text-gray-600">Serves:</label>
          <input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            value={servingInput}
            onChange={(e) => setServingInput(e.target.value)}
            className="w-16 px-2 py-1 border rounded text-sm"
          />
          <button
            onClick={handleGo}
            className="bg-charcoal text-white px-3 py-1 rounded text-sm hover:bg-gray-800 transition"
          >
            Go
          </button>
        </div>
      )}

<div className="flex items-center mt-4 gap-3">
  {/* Cook Button (Primary) */}
  <button
    onClick={() => navigate(`/cook/${recipe._id}?serve=${servingInput}`)}
    className="flex-1 flex items-center justify-center gap-2 bg-charcoal text-white py-2.5 rounded-lg text-sm font-medium transition-transform duration-150 hover:scale-105 hover:brightness-110 active:scale-95"
  >
    <FireIcon className="h-5 w-5" />
    Cook It
  </button>

  {/* Edit Button (Secondary) */}
  <button
    onClick={async () => {
      try {
        const res = await apiClient.post("/chat/recipe-edit", {
          recipeId: recipe._id,
        });
        navigate(`/chat/${res.data.conversationId}`);
      } catch (err) {
        console.error("Error starting recipe edit chat:", err);
      }
    }}
    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/80 text-charcoal text-sm shadow hover:bg-gray-100 hover:text-sage transition-colors"
    title="Edit this recipe with AI"
  >
    <PencilSquareIcon className="h-5 w-5" />
    Edit Recipe
  </button>
</div>


    <div>
    {/* Toggle Tabs */}
    <div className="flex pt-4 mb-4 border-b text-sm">
      <button
        onClick={() => setActiveTab("ingredients")}
        className={`flex-1 py-2 font-medium border-b-2 transition ${
          activeTab === "ingredients"
            ? "border-sage text-sage"
            : "border-transparent text-gray-500 hover:text-sage"
        }`}
      >
        Ingredients
      </button>
      <button
        onClick={() => setActiveTab("instructions")}
        className={`flex-1 py-2 font-medium border-b-2 transition ${
          activeTab === "instructions"
            ? "border-sage text-sage"
            : "border-transparent text-gray-500 hover:text-sage"
        }`}
      >
        Instructions
      </button>
    </div>

    {/* Ingredients Tab */}
    {activeTab === "ingredients" && (
      <div>
        <ul className = "space-y-2" > {recipe.ingredients.map((item, i) => item.isHeading ? ( 
        <li key = {i}
          className = "font-semibold mt-4" > {item.text} 
        </li> ) : ( 
        <li key={i}> 
          <label className="flex items-center gap-2"> 
            <input type="checkbox" checked={checked[i]} onChange={() => toggle(i)} /> 
            <span className = {checked[i] ? "line-through text-gray-500" : ""} > 
              {/*if item.quantity then show this too */ } 
              {item.quantity && item.quantity !== "" && (item.quantity + " ")} 
              {item.text} 
            </span> 
          </label > 
        </li> ) )} 
      </ul>

        <button
          onClick={copyChecklist}
          className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium 
                    text-charcoal bg-white/80 shadow hover:bg-gray-100 hover:text-sage 
                    transition-colors"
        >
          <ClipboardDocumentListIcon className="h-5 w-5" />
          Copy Checklist
        </button>
      </div>
    )}

    {/* Instructions Tab */}
    {activeTab === "instructions" && (
      <div>
        
        <ol className="list-decimal prose list-inside space-y-3">
          {recipe.instructions.map((step, i) => (
            <li key={i}>
              <ReactMarkdown
                components={{ p: ({ node, ...props }) => <span {...props} /> }}
              >
                {step}
              </ReactMarkdown>
            </li>
          ))}
        </ol>
        
      </div>
    )}
  </div>
  {recipe.nutrition && (
        <div className="mt-4 border rounded p-2 text-sm text-gray-600 bg-gray-50">
          <div className="font-semibold mb-1">Nutrition per serving (Approx.)</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div>Calories: {recipe.nutrition.calories} kcal</div>
            <div>Protein: {recipe.nutrition.protein_g} g</div>
            <div>Carbs: {recipe.nutrition.carbohydrates_g} g</div>
            <div>Fat: {recipe.nutrition.fat_g} g</div>
            <div>Saturated Fat: {recipe.nutrition.saturated_fat_g} g</div>
            <div>Fiber: {recipe.nutrition.fiber_g} g</div>
            <div>Sugar: {recipe.nutrition.sugar_g} g</div>
            <div>Salt: {recipe.nutrition.salt_g} g</div>
          </div>
        </div>
      )}
    </div>
  );
}
